/**
 * Delivery Estimate Web Component
 *
 * Detects customer country via IP geolocation (free, open-source APIs),
 * matches against Shopify metaobject delivery rules, and renders
 * an estimated delivery date range on the product page.
 *
 * Priority: exact country match > regional match > default fallback
 */

/**
 * ISO 3166 country → continent code mapping.
 * EU = Europe, NA = North America, SA = South America,
 * AS = Asia, AF = Africa, OC = Oceania, AN = Antarctica
 */
const COUNTRY_TO_CONTINENT = {
  AD: 'EU',
  AE: 'AS',
  AF: 'AS',
  AG: 'NA',
  AI: 'NA',
  AL: 'EU',
  AM: 'AS',
  AO: 'AF',
  AQ: 'AN',
  AR: 'SA',
  AS: 'OC',
  AT: 'EU',
  AU: 'OC',
  AW: 'NA',
  AX: 'EU',
  AZ: 'AS',
  BA: 'EU',
  BB: 'NA',
  BD: 'AS',
  BE: 'EU',
  BF: 'AF',
  BG: 'EU',
  BH: 'AS',
  BI: 'AF',
  BJ: 'AF',
  BL: 'NA',
  BM: 'NA',
  BN: 'AS',
  BO: 'SA',
  BQ: 'NA',
  BR: 'SA',
  BS: 'NA',
  BT: 'AS',
  BV: 'AN',
  BW: 'AF',
  BY: 'EU',
  BZ: 'NA',
  CA: 'NA',
  CC: 'AS',
  CD: 'AF',
  CF: 'AF',
  CG: 'AF',
  CH: 'EU',
  CI: 'AF',
  CK: 'OC',
  CL: 'SA',
  CM: 'AF',
  CN: 'AS',
  CO: 'SA',
  CR: 'NA',
  CU: 'NA',
  CV: 'AF',
  CW: 'NA',
  CX: 'AS',
  CY: 'EU',
  CZ: 'EU',
  DE: 'EU',
  DJ: 'AF',
  DK: 'EU',
  DM: 'NA',
  DO: 'NA',
  DZ: 'AF',
  EC: 'SA',
  EE: 'EU',
  EG: 'AF',
  EH: 'AF',
  ER: 'AF',
  ES: 'EU',
  ET: 'AF',
  FI: 'EU',
  FJ: 'OC',
  FK: 'SA',
  FM: 'OC',
  FO: 'EU',
  FR: 'EU',
  GA: 'AF',
  GB: 'EU',
  GD: 'NA',
  GE: 'AS',
  GF: 'SA',
  GG: 'EU',
  GH: 'AF',
  GI: 'EU',
  GL: 'NA',
  GM: 'AF',
  GN: 'AF',
  GP: 'NA',
  GQ: 'AF',
  GR: 'EU',
  GS: 'AN',
  GT: 'NA',
  GU: 'OC',
  GW: 'AF',
  GY: 'SA',
  HK: 'AS',
  HM: 'AN',
  HN: 'NA',
  HR: 'EU',
  HT: 'NA',
  HU: 'EU',
  ID: 'AS',
  IE: 'EU',
  IL: 'AS',
  IM: 'EU',
  IN: 'AS',
  IO: 'AS',
  IQ: 'AS',
  IR: 'AS',
  IS: 'EU',
  IT: 'EU',
  JE: 'EU',
  JM: 'NA',
  JO: 'AS',
  JP: 'AS',
  KE: 'AF',
  KG: 'AS',
  KH: 'AS',
  KI: 'OC',
  KM: 'AF',
  KN: 'NA',
  KP: 'AS',
  KR: 'AS',
  KW: 'AS',
  KY: 'NA',
  KZ: 'AS',
  LA: 'AS',
  LB: 'AS',
  LC: 'NA',
  LI: 'EU',
  LK: 'AS',
  LR: 'AF',
  LS: 'AF',
  LT: 'EU',
  LU: 'EU',
  LV: 'EU',
  LY: 'AF',
  MA: 'AF',
  MC: 'EU',
  MD: 'EU',
  ME: 'EU',
  MF: 'NA',
  MG: 'AF',
  MH: 'OC',
  MK: 'EU',
  ML: 'AF',
  MM: 'AS',
  MN: 'AS',
  MO: 'AS',
  MP: 'OC',
  MQ: 'NA',
  MR: 'AF',
  MS: 'NA',
  MT: 'EU',
  MU: 'AF',
  MV: 'AS',
  MW: 'AF',
  MX: 'NA',
  MY: 'AS',
  MZ: 'AF',
  NA: 'AF',
  NC: 'OC',
  NE: 'AF',
  NF: 'OC',
  NG: 'AF',
  NI: 'NA',
  NL: 'EU',
  NO: 'EU',
  NP: 'AS',
  NR: 'OC',
  NU: 'OC',
  NZ: 'OC',
  OM: 'AS',
  PA: 'NA',
  PE: 'SA',
  PF: 'OC',
  PG: 'OC',
  PH: 'AS',
  PK: 'AS',
  PL: 'EU',
  PM: 'NA',
  PN: 'OC',
  PR: 'NA',
  PS: 'AS',
  PT: 'EU',
  PW: 'OC',
  PY: 'SA',
  QA: 'AS',
  RE: 'AF',
  RO: 'EU',
  RS: 'EU',
  RU: 'EU',
  RW: 'AF',
  SA: 'AS',
  SB: 'OC',
  SC: 'AF',
  SD: 'AF',
  SE: 'EU',
  SG: 'AS',
  SH: 'AF',
  SI: 'EU',
  SJ: 'EU',
  SK: 'EU',
  SL: 'AF',
  SM: 'EU',
  SN: 'AF',
  SO: 'AF',
  SR: 'SA',
  SS: 'AF',
  ST: 'AF',
  SV: 'NA',
  SX: 'NA',
  SY: 'AS',
  SZ: 'AF',
  TC: 'NA',
  TD: 'AF',
  TF: 'AN',
  TG: 'AF',
  TH: 'AS',
  TJ: 'AS',
  TK: 'OC',
  TL: 'AS',
  TM: 'AS',
  TN: 'AF',
  TO: 'OC',
  TR: 'AS',
  TT: 'NA',
  TV: 'OC',
  TW: 'AS',
  TZ: 'AF',
  UA: 'EU',
  UG: 'AF',
  UM: 'OC',
  US: 'NA',
  UY: 'SA',
  UZ: 'AS',
  VA: 'EU',
  VC: 'NA',
  VE: 'SA',
  VG: 'NA',
  VI: 'NA',
  VN: 'AS',
  VU: 'OC',
  WF: 'OC',
  WS: 'OC',
  XK: 'EU',
  YE: 'AS',
  YT: 'AF',
  ZA: 'AF',
  ZM: 'AF',
  ZW: 'AF',
};

class DeliveryEstimate extends HTMLElement {
  static SESSION_KEY = 'de_country_code';

  constructor() {
    super();
    this.estimateDiv = null;
    this.locale = null;
    this.abortController = new AbortController();
    this.madeToOrderInformation = null;
    this.isMto = null;
  }

  connectedCallback() {
    this.init();
  }

  async init() {
    this.estimateDiv = this.querySelector('[data-estimate-div]');

    this.madeToOrderInformation = this.dataset.madeToOrderInformation || null;
    this.isMto = this.dataset.isMto === 'true';

    if (this.madeToOrderInformation && this.isMto) {
      this.estimateDiv.textContent = this.madeToOrderInformation;
      this.classList.remove('hidden');
      return;
    }

    this.locale = this.dataset.locale || 'en-US';
    const estimates = this.getEstimatesData();
    if (!estimates?.length || !this.estimateDiv) return;

    const countryCode = await this.resolveCountryCode();
    const match = this.findEstimate(estimates, countryCode);

    if (match) {
      this.classList.remove('hidden');
      this.render(match);
    } else {
      this.estimateDiv.textContent = '';
      this.classList.add('hidden');
    }
  }

  // ─── Data ──────────────────────────────────────────────────────────────────

  getEstimatesData() {
    try {
      const el = document.getElementById('delivery-estimates-data');
      return el ? JSON.parse(el.textContent) : null;
    } catch {
      return null;
    }
  }

  // ─── Geolocation cascade ───────────────────────────────────────────────────

  async resolveCountryCode() {
    // 1. Liquid-provided country (from Shopify localization selector — most reliable)
    const liquidCountry = this.dataset.shopifyCountry?.trim().toUpperCase();
    if (liquidCountry && liquidCountry.length === 2) return liquidCountry;

    // 2. sessionStorage cache (avoid repeated API calls)
    const cached = sessionStorage.getItem(DeliveryEstimate.SESSION_KEY);
    if (cached) return cached;

    // 3. Free open-source geolocation APIs (cascade on failure)
    const code = await this.fetchCountryFromIp();
    if (code) {
      sessionStorage.setItem(DeliveryEstimate.SESSION_KEY, code);
      return code;
    }

    return null;
  }

  async fetchCountryFromIp() {
    const providers = [
      // api.country.is — completely open-source, no key required
      {
        url: 'https://api.country.is/',
        parse: (d) => d?.country,
      },
      // ipapi.co — free tier (1 000 req/day), plain-text endpoint
      {
        url: 'https://ipapi.co/country_code/',
        parse: (text) =>
          typeof text === 'string' && text.length === 2 ? text : null,
        isText: true,
      },
    ];

    for (const provider of providers) {
      try {
        const res = await fetch(provider.url, {
          signal: AbortSignal.any([
            this.abortController.signal,
            AbortSignal.timeout(2000),
          ]),
        });
        if (!res.ok) continue;
        const data = provider.isText ? await res.text() : await res.json();
        const code = provider.parse(data)?.trim().toUpperCase();
        if (code?.length === 2) return code;
      } catch {
        // try next provider
      }
    }

    return null;
  }

  // ─── Matching logic ────────────────────────────────────────────────────────

  findEstimate(estimates, countryCode) {
    if (!countryCode) {
      return estimates.find((e) => e.isDefault) ?? null;
    }

    const code = countryCode.toUpperCase();

    // Exact single-country match (highest priority — e.g. "CA" beats "North America")
    const exact = estimates.find(
      (e) => e.countryCodes.length === 1 && e.countryCodes[0] === code
    );
    if (exact) return exact;

    // Regional / multi-country match
    const regional = estimates.find((e) => e.countryCodes.includes(code));
    if (regional) return regional;

    // Continent auto-match (country's continent matches regionCode)
    const continent = COUNTRY_TO_CONTINENT[code];
    if (continent) {
      const continentMatch = estimates.find((e) => e.regionCode === continent);
      if (continentMatch) return continentMatch;
    }

    // Default fallback
    return estimates.find((e) => e.isDefault) ?? null;
  }

  // ─── Date helpers ──────────────────────────────────────────────────────────

  /**
   * Add `days` to `date`. When `businessDays` is true, skips Sat & Sun.
   */
  addDays(date, days, businessDays) {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      if (!businessDays || (result.getDay() !== 0 && result.getDay() !== 6)) {
        added++;
      }
    }
    return result;
  }

  formatDate(date) {
    return date.toLocaleDateString(this.locale, {
      month: 'short',
      day: 'numeric',
    });
  }

  isSameMonth(a, b) {
    return a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  }

  /**
   * Format range: "Mar 13–15" (same month) or "Mar 28 – Apr 2" (different months)
   */
  formatRange(minDate, maxDate) {
    if (minDate.getTime() === maxDate.getTime()) {
      return this.formatDate(minDate);
    }
    if (this.isSameMonth(minDate, maxDate)) {
      const month = minDate.toLocaleDateString(this.locale, { month: 'short' });
      return `${minDate.getDate()}–${maxDate.getDate()} ${month}`;
    }
    return `${this.formatDate(minDate)} – ${this.formatDate(maxDate)}`;
  }

  render(estimate) {
    const today = new Date();
    const minDate = this.addDays(
      today,
      estimate.minDays,
      estimate.businessDays
    );
    const maxDate = this.addDays(
      today,
      estimate.maxDays,
      estimate.businessDays
    );

    const dateRange = this.formatRange(minDate, maxDate);

    this.estimateDiv.textContent = dateRange;
  }

  disconnectedCallback() {
    this.abortController.abort();
  }
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}
